import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { BellIcon, PhoneIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface HipaaReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (params: {
    phoneNumber: string;
    smsMessage?: string;
  }) => Promise<void>;
  isSubmitting?: boolean;
  /** Original phone number the document was sent to */
  originalPhone?: string | null;
  /** Original email the document was sent to */
  originalEmail?: string | null;
  /** Client name for display */
  clientName?: string | null;
  /** Firm name for SMS message template */
  firmName?: string | null;
  /** Z-index for the modal (use Z_INDEX_MODAL_NESTED for nested modals) */
  zIndex?: number;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export const HipaaReminderModal: React.FC<HipaaReminderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  originalPhone,
  originalEmail,
  clientName,
  firmName,
  zIndex,
}) => {
  // Build default SMS message with firm name
  const defaultSmsMessage = firmName
    ? `This is Finch Records Services reaching out on behalf of ${firmName} to remind you to sign your HIPAA Authorization.`
    : 'This is a reminder to sign your HIPAA Authorization.';

  const [phoneNumber, setPhoneNumber] = useState(originalPhone ?? '');
  const [showCustomMessage, setShowCustomMessage] = useState(false);
  const [customMessage, setCustomMessage] = useState(defaultSmsMessage);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const prevIsOpen = useRef(isOpen);

  // Handle success/error state timing
  useEffect(() => {
    if (submitStatus === 'success') {
      const timer = setTimeout(() => {
        onClose();
        // Reset after close animation
        setTimeout(() => setSubmitStatus('idle'), 200);
      }, 1000);
      return () => clearTimeout(timer);
    }
    if (submitStatus === 'error') {
      const timer = setTimeout(() => {
        setSubmitStatus('idle');
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [submitStatus, onClose]);

  // Reset state only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      // Modal just opened - reset state
      setSubmitStatus('idle');
      setPhoneNumber(originalPhone ?? '');
      setShowCustomMessage(false);
      setCustomMessage(defaultSmsMessage);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, originalPhone, defaultSmsMessage]);

  // Update SMS message when firmName becomes available (async load)
  useEffect(() => {
    const genericDefault =
      'This is a reminder to sign your HIPAA Authorization.';
    if (firmName && customMessage === genericDefault) {
      setCustomMessage(defaultSmsMessage);
    }
  }, [firmName, defaultSmsMessage, customMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitStatus('submitting');
    try {
      await onSubmit({
        phoneNumber,
        smsMessage:
          showCustomMessage && customMessage ? customMessage : undefined,
      });
      setSubmitStatus('success');
    } catch {
      setSubmitStatus('error');
    }
  };

  const handleClose = () => {
    if (submitStatus === 'submitting') return; // Prevent closing while submitting
    setPhoneNumber(originalPhone ?? '');
    setShowCustomMessage(false);
    setCustomMessage(defaultSmsMessage);
    setSubmitStatus('idle');
    onClose();
  };

  const isValidPhone = phoneNumber.replace(/\D/g, '').length >= 10;
  const isButtonBusy =
    isSubmitting ||
    submitStatus === 'submitting' ||
    submitStatus === 'success' ||
    submitStatus === 'error';
  const canSubmit = isValidPhone && !isButtonBusy;

  const isPhoneChanged =
    originalPhone &&
    phoneNumber.replace(/\D/g, '') !== originalPhone.replace(/\D/g, '');

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" zIndex={zIndex}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <BellIcon className="h-4 w-4 text-amber-600" />
            </div>
            Send SMS Reminder
          </DialogTitle>
          <DialogDescription>
            Send a reminder to the client to sign their HIPAA Authorization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Original recipient info */}
          {(originalEmail || clientName) && (
            <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
              <span className="font-medium">Original recipient: </span>
              {clientName && <span>{clientName}</span>}
              {clientName && originalEmail && <span> &lt;</span>}
              {originalEmail && <span>{originalEmail}</span>}
              {clientName && originalEmail && <span>&gt;</span>}
            </div>
          )}

          {/* Phone Number Input */}
          <div className="space-y-2">
            <Label
              htmlFor="phone"
              className="text-sm font-medium flex items-center gap-2"
            >
              <PhoneIcon className="h-4 w-4 text-gray-500" />
              Phone Number
            </Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            {phoneNumber && !isValidPhone && (
              <p className="text-xs text-amber-600">
                Please enter a valid 10-digit phone number
              </p>
            )}
            {isPhoneChanged && isValidPhone && (
              <p className="text-xs text-blue-600">
                Reminder will be sent to this new number instead of the original
              </p>
            )}
          </div>

          {/* Customize Message (collapsible) */}
          <div className="border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowCustomMessage(!showCustomMessage)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium">Customize SMS message</span>
              {showCustomMessage ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>
            {showCustomMessage && (
              <div className="px-3 pb-3 pt-1 border-t bg-gray-50">
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Enter a custom message to include with the signing link..."
                  className="text-sm resize-none"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Leave blank to use the default reminder message.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isButtonBusy}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className={cn(
                'gap-2 transition-colors',
                submitStatus === 'success' &&
                  'bg-green-600 hover:bg-green-600 text-white',
                submitStatus === 'error' &&
                  'bg-red-600 hover:bg-red-600 text-white',
              )}
            >
              {submitStatus === 'submitting' ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Sending...
                </>
              ) : submitStatus === 'success' ? (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Sent!
                </>
              ) : submitStatus === 'error' ? (
                <>
                  <XMarkIcon className="h-4 w-4" />
                  Failed
                </>
              ) : (
                <>
                  <BellIcon className="h-4 w-4" />
                  Send Reminder
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
